import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1583345237708-add35a664d77?crop=entropy&cs=srgb&fm=webp&q=80&w=1920";

export default function Hero({ onOpenEstimate }) {
  const bgRef = useRef(null);

  // Subtle parallax
  useEffect(() => {
    const onScroll = () => {
      if (!bgRef.current) return;
      const y = window.scrollY;
      bgRef.current.style.transform = `translate3d(0, ${y * 0.18}px, 0) scale(1.08)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain bg-[var(--crest-charcoal)]"
    >
      {/* Background image with parallax */}
      <div
        ref={bgRef}
        role="img"
        aria-label="Steep residential rooftop at golden hour — Crestline Roofing Ltd. Vancouver"
        className="absolute inset-0 will-change-transform"
        style={{
          backgroundImage: `url('${HERO_IMG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
          transform: "scale(1.08)",
        }}
      />

      {/* Tonal overlays — slate + amber bottom warmth */}
      <div className="absolute inset-0 bg-gradient-to-b from-[rgba(18,18,18,0.55)] via-[rgba(18,18,18,0.45)] to-[rgba(18,18,18,0.95)]" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[rgba(217,119,6,0.12)] via-transparent to-[rgba(30,41,59,0.45)]" />

      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-28 pb-16">
        {/* Eyebrow */}
        <div className="reveal flex items-center gap-3 mb-8">
          <span className="h-px w-12 bg-[var(--crest-amber)]" />
          <span
            data-testid="hero-eyebrow"
            className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]"
          >
            Premium Roofing — Est. 2010
          </span>
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="reveal font-display uppercase font-extrabold leading-[0.92] tracking-tight text-white"
          style={{ fontSize: "clamp(2.4rem, 8vw, 7.5rem)" }}
        >
          Vancouver&apos;s Trusted
          <br />
          <span className="text-[var(--crest-amber)]">Roofing Experts.</span>
        </h1>

        {/* Tagline (subtitle directly below H1) */}
        <p
          data-testid="hero-tagline"
          className="reveal font-display uppercase font-bold tracking-tight text-white/90 mt-6"
          style={{
            fontSize: "clamp(1.3rem, 2.6vw, 2rem)",
            transitionDelay: "80ms",
          }}
        >
          Built to Last.{" "}
          <span className="text-[var(--crest-amber)]">Trusted in Vancouver.</span>
        </p>

        {/* Subline */}
        <p
          data-testid="hero-subline"
          className="reveal font-body text-base md:text-lg text-white/70 mt-6 max-w-2xl leading-relaxed"
          style={{ transitionDelay: "180ms" }}
        >
          Premium roofing installation, repair, and inspection — done right the
          first time. Storm-tested craftsmanship from a team that treats your
          home like ours.
        </p>

        {/* CTA */}
        <div className="reveal mt-12 flex flex-wrap items-center gap-6" style={{ transitionDelay: "300ms" }}>
          <button
            data-testid="hero-cta-estimate"
            onClick={onOpenEstimate}
            className="amber-pulse group inline-flex items-center gap-3 font-display uppercase tracking-wider font-bold text-base md:text-lg px-9 py-5 rounded-sm bg-[var(--crest-amber)] text-[#121212] hover:bg-[var(--crest-amber-bright)] transition-colors duration-300"
          >
            Get a Free Estimate
            <ArrowRight
              size={20}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
          <a
            href="tel:+17789176817"
            data-testid="hero-cta-call"
            className="font-body text-sm md:text-base text-white/80 hover:text-[var(--crest-amber)] transition-colors flex items-center gap-2"
          >
            <span className="font-display uppercase tracking-widest text-xs text-white/50">
              or call
            </span>
            <span className="font-semibold">(778) 917-6817</span>
          </a>
        </div>

        {/* Scroll indicator removed */}
      </div>
    </section>
  );
}
